import {
	DocumentDetails
 } from 'sharebase-app/components/documentDetails/documentDetails';
export interface DocumentDetailsDataStore {
	load(docId:number) : ng.IPromise<DocumentDetails>;
}	

/**
* @ngdoc service
* @name  sharebase-app.sbaDocumentDetailsDataStore
* 
* @description 
* A datastore used to retrieve the details of a Document.
* 
**/
export function DocumentDetailsDataStoreFactory(
	$q:ng.IQService, 
	sbDocumentDetailService, 
	utils
) {
	let _details: DocumentDetails = {} as DocumentDetails;
	let _documentId = null;
	let _sharedUsers = null;
	let _sharedGroups = null;
	let _sharedLinks = null;

	// flag used to determine if the details panel is open
	let _isOpen = false;

	return {
		/**
		* @ngdoc method
		* @name sbaDocumentDetailsDataStore#loadDocumentDetails
		* @methodOf sharebase-app.sbaDocumentDetailsDataStore
		* @kind function
		*
		* @description
		* Loads a document's details
		* 
		* @param {object} documentId A Document ID
		* @param {Function} cb A callback that will we executed when the load has completed or encountered an error.
		*/
		load: utils.debounce(load, 300),

		/**
		* @ngdoc property
		* @name sbaDocumentDetailsDataStore#details
		* @propertyOf sharebase-app.sbaDocumentDetailsDataStore
		* @kind property
		*
		* @description
		* A given document's details
		* 
		* @returns {array} An object of document's details
		*/
		get details() { return _details; },

		/**
		* @ngdoc property
		* @name sbaDocumentDetailsDataStore#activities
		* @propertyOf sharebase-app.sbaDocumentDetailsDataStore
		* @kind property
		*
		* @description
		* A given document's activities
		* 
		* @returns {array} An array of a selected document's activities
		*/
		get activities() { return _details.activities; },

		/**
		* @ngdoc property
		* @name sbaDocumentDetailsDataStore#comments
		* @propertyOf sharebase-app.sbaDocumentDetailsDataStore
		* @kind property
		*
		* @description
		* A given document's comments
		* 
		* @returns {array} An array of a selected document's comments
		*/
		get comments() { return _details.comments; },

		/**
		* @ngdoc property
		* @name sbaDocumentDetailsDataStore#versions
		* @propertyOf sharebase-app.sbaDocumentDetailsDataStore
		* @kind property
		*
		* @description
		* A given document's versions
		* 
		* @returns {array} An array of a selected document's versions
		*/
		get versions() { return _details.versions; },

		/**
		* @ngdoc property
		* @name sbaDocumentDetailsDataStore#sharedLinks
		* @propertyOf sharebase-app.sbaDocumentDetailsDataStore
		* @kind property
		*
		* @description
		* A given document's shared links
		* 
		* @returns {array} An array of a selected document's shared links
		*/
		get sharedLinks() { return _sharedLinks; },

		/**
		* @ngdoc property
		* @name sbaDocumentDetailsDataStore#sharedUsers
		* @propertyOf sharebase-app.sbaDocumentDetailsDataStore
		* @kind property
		*
		* @description
		* A given document's shared users 
		* 
		* @returns {array} An array of a selected document's shared users
		*/
		get sharedUsers() { return _sharedUsers; },

		/**
		* @ngdoc property
		* @name sbaDocumentDetailsDataStore#sharedGroups
		* @propertyOf sharebase-app.sbaDocumentDetailsDataStore
		* @kind property
		*
		* @description
		* A given document's shared groups (and user groups)
		* 
		* @returns {array} An array of a selected document's shared groups
		*/
		get sharedGroups() { return _sharedGroups; },

		/**
		 * @ngdoc property
		 * @name sbaDocumentDetailsDataStore#isOpen
		 * @propertyOf sharebase-app.sbaDocumentDetailsDataStore
		 * @kind property
		 * 
		 * @description
		 * Flag used to determine if the details panel is open.
		 */
		set isOpen(val) { _isOpen = val; },
		get isOpen() { return _isOpen; },

		/**
		 * @ngdoc method
		 * @name sbaDocumentDetailsDataStore#updateDetails
		 * @methodOf sharebase-app.sbaDocumentDetailsDataStore
		 * @kind function
		 * 
		 * @description
		 * Reload the document details if, and only if, the details panel is open,
		 * and the provided document id is the same as the previously loaded document
		 * details.
		 */
		reloadDetails
	};

	function load(documentId) {
		if (typeof documentId === 'string') {
			documentId = parseInt(documentId, 10);
		}
		_documentId = documentId;
		return sbDocumentDetailService.getDocumentDetails(documentId)
			.then(function (response) {
				_details = {} as DocumentDetails;
				_details.dateCreated = response.dateCreated;
				_details.dateModified = response.dateModified;
				_details.fileFormat = response.fileFormat;
				_details.fileSize = response.fileSize;
				_details.owner = response.owner;

				return $q.all([
					sbDocumentDetailService.getActivities(documentId),
					sbDocumentDetailService.getComments(documentId),
					sbDocumentDetailService.getVersions(documentId),
					sbDocumentDetailService.getSharedLinks(documentId),
					sbDocumentDetailService.getSharedUsers(documentId)
				]);
			})
			.then(function (responses) {
				_details.activities = responses[0];
				_details.comments = responses[1];
				_details.versions = responses[2];
				_sharedLinks = responses[3].filter(function(link){return !link.IsExpired;});
				_sharedUsers = responses[4].filter(function(share){ return share.DocumentACLId !== -1 && share.UserGroupId === 0;});
				_sharedGroups = responses[4].filter(function(share){ return share.UserGroupId !== 0;});

				return _details;
			});
	}

	function reloadDetails(documentId) {
		if (_isOpen && documentId === _documentId) {
			load(documentId);
		}
	}
}

DocumentDetailsDataStoreFactory.$inject = [
	'$q', 
	'sbDocumentDetailService',
	'utils'
];


// WEBPACK FOOTER //
// ./src/sharebase-app/dataStores/documentDetailsDataStore.ts